<script setup>
import { ref, computed, watch, onMounted } from 'vue';
import { useStore } from 'dashboard/composables/store';
import { useAccount } from 'dashboard/composables/useAccount';
import ContactDatePicker from './ContactDatePicker.vue';
import { getEventConfig } from './contactTrackingConfig';

const props = defineProps({
  contactId: { type: Number, required: true },
});

const store = useStore();
const { accountId } = useAccount();

const ALL_EVENT_TYPES = [
  'add_payment_info', 'add_shipping_info', 'add_to_cart', 'add_to_wishlist',
  'begin_checkout', 'click', 'close_convert_lead', 'close_unconvert_lead',
  'disqualify_lead', 'generate_lead', 'page_view', 'purchase',
  'qualify_lead', 'refund', 'search', 'session_start', 'view_content',
];

const PAGE_SIZE = 8;
const currentPage = ref(1);
const activeFilter = ref('all');
const dateRange = ref(null);

const isLoading = computed(() => store.getters['contactEvents/getUIFlags'].isFetching);
const paginated  = computed(() => store.getters['contactEvents/getEvents'](props.contactId));
const storeMeta  = computed(() => store.getters['contactEvents/getMeta'](props.contactId));
const totalPages = computed(() => storeMeta.value.total_pages || 1);
const totalCount = computed(() => storeMeta.value.total_count || 0);

const fromTo = computed(() => {
  if (!totalCount.value) return { start: 0, end: 0, total: 0 };
  const start = (currentPage.value - 1) * PAGE_SIZE + 1;
  const end   = Math.min(currentPage.value * PAGE_SIZE, totalCount.value);
  return { start, end, total: totalCount.value };
});

const paginationText = computed(() => `${fromTo.value.start}–${fromTo.value.end} de ${fromTo.value.total}`);
const pageText = computed(() => `${currentPage.value} / ${totalPages.value}`);

const fetchPage = () => {
  const params = { page: currentPage.value, per_page: PAGE_SIZE };
  if (activeFilter.value !== 'all') params.event_type = activeFilter.value;
  if (dateRange.value?.[0]) params.start_date = dateRange.value[0].toISOString().substring(0, 10);
  if (dateRange.value?.[1]) params.end_date   = dateRange.value[1].toISOString().substring(0, 10);
  store.dispatch('contactEvents/fetchEvents', {
    accountId: accountId.value,
    contactId: props.contactId,
    params,
  });
};

const resetPage = () => { currentPage.value = 1; };

const onDateRangeChanged = ([start, end]) => {
  dateRange.value = [start, end];
  resetPage();
  fetchPage();
};

watch(currentPage, fetchPage);
watch(activeFilter, () => { resetPage(); fetchPage(); });
watch(() => props.contactId, () => { resetPage(); fetchPage(); });

onMounted(fetchPage);

const getEventMeta = type => getEventConfig(type);

const formatDate = iso => {
  const d = new Date(iso);
  const months = ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez'];
  const time = `${String(d.getHours()).padStart(2,'0')}:${String(d.getMinutes()).padStart(2,'0')}`;
  return `${d.getDate()} de ${months[d.getMonth()]}. · ${time}`;
};

const formatCurrency = val => {
  if (!val) return null;
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(val);
};
</script>

<template>
  <div class="flex flex-col gap-0">

    <!-- Header + Filters -->
    <div class="flex items-center justify-between gap-3 px-4 py-4 border-b border-n-weak flex-wrap">
      <!-- Title -->
      <div class="min-w-0">
        <h3 class="text-sm font-medium text-n-slate-12 truncate">
          {{ $t('CONTACT_METRICS.TIMELINE.TITLE') }}
        </h3>
        <p class="text-xs text-n-slate-10 mt-0.5">
          {{ totalCount }} {{ $t('CONTACT_METRICS.TIMELINE.EVENTS_IN_FILTER') }}
        </p>
      </div>

      <!-- Controls — alinhados à direita, largura natural -->
      <div class="flex items-center gap-2 flex-shrink-0">
        <!-- Event type select -->
        <div class="select-wrap relative inline-flex items-center">
          <select
            v-model="activeFilter"
            class="cw-select !mb-0 !h-8 !py-1 text-xs w-36 cursor-pointer"
            @change="resetPage"
          >
            <option value="all">{{ $t('CONTACT_METRICS.TIMELINE.FILTER_ALL') }}</option>
            <option v-for="type in ALL_EVENT_TYPES" :key="type" :value="type">{{ type }}</option>
          </select>
          <span class="absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none i-lucide-chevron-down size-3 text-n-slate-10" />
        </div>

        <!-- Date picker — calendário único com atalhos -->
        <ContactDatePicker @date-range-changed="onDateRangeChanged" />
      </div>
    </div>

    <!-- Loading skeleton -->
    <div v-if="isLoading" class="flex flex-col items-center justify-center py-16 gap-2">
      <span class="i-lucide-loader-2 size-6 text-n-slate-10 animate-spin" />
    </div>

    <!-- Empty state -->
    <div v-else-if="!paginated.length" class="flex flex-col items-center justify-center py-16 gap-2">
      <span class="i-lucide-calendar-x size-8 text-n-slate-10" />
      <p class="text-sm text-n-slate-10">{{ $t('CONTACT_METRICS.TIMELINE.NO_EVENTS') }}</p>
    </div>

    <!-- Table -->
    <div v-else class="overflow-x-auto">
      <table class="w-full table-auto">
        <thead>
          <tr class="border-b border-n-weak">
            <th class="px-6 py-3 text-left text-xs font-medium text-n-slate-10 uppercase tracking-wider w-52">
              {{ $t('CONTACT_METRICS.TIMELINE.COL_EVENT') }}
            </th>
            <th class="px-4 py-3 text-left text-xs font-medium text-n-slate-10 uppercase tracking-wider w-48">
              {{ $t('CONTACT_METRICS.TIMELINE.COL_DOMAIN') }}
            </th>
            <th class="px-4 py-3 text-left text-xs font-medium text-n-slate-10 uppercase tracking-wider">
              {{ $t('CONTACT_METRICS.TIMELINE.COL_PAGE') }}
            </th>
            <th class="px-4 py-3 text-left text-xs font-medium text-n-slate-10 uppercase tracking-wider w-52">
              {{ $t('CONTACT_METRICS.TIMELINE.COL_SOURCE') }}
            </th>
            <th class="px-4 py-3 text-right text-xs font-medium text-n-slate-10 uppercase tracking-wider w-32">
              {{ $t('CONTACT_METRICS.TIMELINE.COL_VALUE') }}
            </th>
            <th class="px-6 py-3 text-right text-xs font-medium text-n-slate-10 uppercase tracking-wider w-44">
              {{ $t('CONTACT_METRICS.TIMELINE.COL_DATE') }}
            </th>
          </tr>
        </thead>
        <tbody class="divide-y divide-n-weak">
          <tr
            v-for="event in paginated"
            :key="event.id"
            class="hover:bg-n-alpha-1 transition-colors"
          >
            <!-- Evento -->
            <td class="px-6 py-3">
              <div class="flex items-center gap-2.5">
                <span
                  class="flex items-center justify-center w-7 h-7 rounded-lg flex-shrink-0"
                  :class="getEventMeta(event.event_type).bg"
                >
                  <span
                    class="size-3.5"
                    :class="[getEventMeta(event.event_type).icon, getEventMeta(event.event_type).color]"
                  />
                </span>
                <span class="text-sm font-medium text-n-slate-12 whitespace-nowrap">
                  {{ event.event_type }}
                </span>
              </div>
            </td>
            <!-- Domínio -->
            <td class="px-4 py-3">
              <span class="text-xs font-medium text-n-slate-11 font-mono">
                {{ event.domain }}
              </span>
            </td>
            <!-- Página -->
            <td class="px-4 py-3">
              <span class="text-n-slate-10 font-mono text-xs">
                {{ event.url }}
              </span>
            </td>
            <!-- Origem -->
            <td class="px-4 py-3">
              <span class="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-n-solid-3 text-n-slate-11 border border-n-weak">
                <span class="size-1.5 rounded-full bg-current opacity-70" />
                {{ event.source }}
              </span>
            </td>
            <!-- Valor -->
            <td class="px-4 py-3 text-right">
              <span v-if="event.value" class="text-sm font-medium text-n-teal-11">
                {{ formatCurrency(event.value) }}
              </span>
              <span v-else class="text-sm text-n-slate-10">—</span>
            </td>
            <!-- Data -->
            <td class="px-6 py-3 text-right">
              <span class="text-xs text-n-slate-10 whitespace-nowrap">
                {{ formatDate(event.occurred_at) }}
              </span>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Pagination -->
    <div v-if="totalCount" class="flex items-center justify-between px-6 py-3 border-t border-n-weak">
      <span class="text-xs text-n-slate-10">
        {{ paginationText }}
      </span>
      <div class="flex items-center gap-2">
        <button
          class="flex items-center gap-1 px-3 py-1.5 rounded-md text-xs font-medium transition-colors"
          :class="currentPage <= 1
            ? 'text-n-slate-10 cursor-not-allowed opacity-40'
            : 'text-n-slate-11 hover:bg-n-alpha-2'"
          :disabled="currentPage <= 1"
          @click="currentPage--"
        >
          <span class="i-lucide-chevron-left size-3" />
          {{ $t('CONTACT_METRICS.TIMELINE.PREV') }}
        </button>
        <span class="text-xs font-medium text-n-slate-12 px-2">
          {{ pageText }}
        </span>
        <button
          class="flex items-center gap-1 px-3 py-1.5 rounded-md text-xs font-medium transition-colors"
          :class="currentPage >= totalPages
            ? 'text-n-slate-10 cursor-not-allowed opacity-40'
            : 'text-n-slate-11 hover:bg-n-alpha-2'"
          :disabled="currentPage >= totalPages"
          @click="currentPage++"
        >
          {{ $t('CONTACT_METRICS.TIMELINE.NEXT') }}
          <span class="i-lucide-chevron-right size-3" />
        </button>
      </div>
    </div>

  </div>
</template>

<style scoped>
/* Select compacto — remove overrides do CSS global de formulários */
.cw-select {
  background-image: none !important;
  background-color: rgba(var(--alpha-2)) !important;
  outline: none !important;
  appearance: none !important;
  padding-right: 1.75rem !important;
  padding-left: 0.75rem !important;
}

.cw-select:hover {
  background-color: rgba(var(--alpha-1)) !important;
}
</style>
